import { lazy, Suspense, type ReactNode, useEffect } from 'react'
import { TopBar } from './TopBar'
import { GroupedRail } from './GroupedRail'
import { ChatRail } from './ChatRail'
import { BottomBar } from './BottomBar'
import { InspectorOverlay } from './inspector-overlay'
import { DevToolsDock } from './devtools-dock'
import { ResourcesDrawer } from './resources-drawer'
import { ExpressionLabDrawer } from '@/features/expression-lab/components/expression-lab-drawer'
import { useStudioStore } from '../../store/studio-store'
import { EditorSurface } from './surfaces/editor-surface'
import { SourceSurface } from './surfaces/source-surface'
import { DataSurface } from './surfaces/data-surface'
import { BehaviorSurface } from './surfaces/behavior-surface'
import { EssentialsBuildSurface } from './surfaces/essentials-build-surface'
import { EssentialsConnectSurface } from './surfaces/essentials-connect-surface'
import { EssentialsReleaseSurface } from './surfaces/essentials-release-surface'
import { PreviewSurface } from './surfaces/preview-surface'
import { CodeSurface } from './surfaces/code-surface'
import { LibrarySurface } from './surfaces/library-surface'
import { MapSurface } from './surfaces/map-surface'
import { EvidenceSurfaceLazy } from './surfaces/evidence-surface-lazy'
import { ReportWorkspaceSurfaceLazy } from './surfaces/report-workspace-surface-lazy'
import { DocumentWorkspaceSurfaceLazy } from './surfaces/document-workspace-surface-lazy'
import { PublishSurface } from './surfaces/publish-surface'
import { DeploySurface } from './surfaces/deploy-surface'
import { SettingsSurface } from './surfaces/settings-surface'
import { EmptyState } from './empty-state'
import { BuildModeCanvas } from './components/build-mode-canvas'
import { OnboardingTour } from './components/onboarding-tour'
import { useUIBreakpoint } from '@/hooks/use-ui-breakpoint'
import { registerShortcuts, unregisterShortcuts } from '@/shell/shortcut-bus'
import { ErrorBoundary } from '@/components/error-boundary'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from './coogo-ide-chrome-copy'
import { CockpitAttentionHandoff } from './cockpit/cockpit-attention-handoff'
import { readAsIsSnapshotFromSettings } from '@/features/preview/services/as-is-snapshot'
import { useAsIsSourceMarkupPresent } from '@/features/preview/hooks/use-as-is-source-markup'
import { getCoogoAsIsPaneCopy } from './coogo-ide-renewal-copy'

interface Props {
  children?: ReactNode
}

// Bundle boundary (2026-08-12 operator ruling, Task 3 round 2): AsIsView
// transitively reaches @cogo/react-renderer-v2 (heavy). A static import here
// would put it in the entry bundle for every project, including users who
// never open a conversion project — same reasoning preview-frame-content.tsx
// already applies to AppV2Preview.
const AsIsView = lazy(() =>
  import('@/features/preview/components/as-is-view').then((m) => ({
    default: m.AsIsView,
  })),
)

/**
 * Visible wait state for the As-Is chunk (M5), in the preview canvas's style.
 *
 * fix-round-4 — this line is the FIRST As-Is words the user sees: it renders
 * while the lazily-imported AsIsView chunk (which pulls the heavy renderer
 * package) downloads, before the pane can say anything. It used to render the
 * derived `loading` wording unconditionally, so on a project carrying
 * `sourceMarkup` it announced "the original structure" and the pane then
 * corrected itself to "the original" once the chunk landed. Exported for the
 * test that pins both states.
 */
export function AsIsLoadingPlaceholder() {
  const { locale } = useStudioLocale()
  const asIsSourceMarkupPresent = useAsIsSourceMarkupPresent()
  const paneCopy = getCoogoAsIsPaneCopy(locale)
  return (
    <div
      data-testid="as-is-loading-placeholder"
      className="flex h-full items-center justify-center text-sm text-muted-foreground"
    >
      {asIsSourceMarkupPresent ? paneCopy.asIsSourceTruthLoading : paneCopy.loading}
    </div>
  )
}

function ActiveSurface({ fallback }: { fallback: ReactNode }) {
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const studioPersona = useStudioStore((s) => s.studioPersona)
  // "as-is" (G1 alternative A) shares the "source" rail item with "design"
  // — As-Is has no rail-item sub-navigation of its own (DOMAIN_RENEWAL_RAIL
  // in studio-authority-workspaces.ts is empty for it), so its lone
  // authority task parks activeRailItem on "source" and this switch
  // disambiguates by authorityDomain, exactly like the studioPersona-gated
  // cases below disambiguate a shared rail item by persona.
  const authorityDomain = useStudioStore((s) => s.chatContext?.authorityDomain)
  // The project's settings row, carried beside the loaded app by
  // `openServerProject` — `LoadedApp` has no `settings` field, so reading
  // `currentProject.settings` here was reading `undefined` forever
  // (FINAL-REVIEW C1).
  const projectSettings = useStudioStore((s) => s.projectSettings)
  switch (activeRailItem) {
    case 'preview':
      return <PreviewSurface />
    case 'source': {
      if (authorityDomain === 'as-is') {
        const snapshot = readAsIsSnapshotFromSettings(projectSettings)
        // The tab itself is gated on a snapshot existing
        // (resolveArtifactModeTabs in artifact-mode-switcher.tsx), but this
        // stays defensive: a null snapshot renders nothing rather than
        // guessing.
        // FINAL-REVIEW M5 — `fallback={null}` here plus a second one inside
        // AsIsView meant two sequential blank panes while two chunks loaded;
        // the reader could not tell "loading" from "nothing to show". Same
        // shape the preview canvas uses for its own chunk wait
        // (CanvasPlaceholder in preview-frame-content.tsx).
        return snapshot ? (
          <Suspense fallback={<AsIsLoadingPlaceholder />}>
            <AsIsView snapshot={snapshot} />
          </Suspense>
        ) : null
      }
      return <SourceSurface />
    }
    case 'editor':
      return studioPersona === 'creator' ? <EssentialsBuildSurface /> : <EditorSurface />
    case 'data':
      return studioPersona === 'creator' ? <EssentialsConnectSurface /> : <DataSurface />
    case 'behavior':
      return studioPersona === 'creator' ? <EssentialsBuildSurface /> : <BehaviorSurface />
    case 'code':
      return <CodeSurface />
    case 'library':
      return <LibrarySurface />
    case 'map':
      return <MapSurface />
    case 'evidence':
      return studioPersona === 'creator' ? <EssentialsReleaseSurface /> : <EvidenceSurfaceLazy />
    case 'report':
      return <ReportWorkspaceSurfaceLazy />
    case 'document':
      return <DocumentWorkspaceSurfaceLazy />
    case 'publish':
      return studioPersona === 'creator' ? <EssentialsReleaseSurface /> : <PublishSurface />
    case 'deploy':
      return studioPersona === 'creator' ? <EssentialsReleaseSurface /> : <DeploySurface />
    case 'settings':
      return <SettingsSurface />
    default:
      return <>{fallback}</>
  }
}

/**
 * ActiveCanvasBody — Phase A A3.3 branch.
 *
 * In build mode with a dispatched router output (Channel 1 hit OR Channel 2
 * stream/result), render BuildModeCanvas. Otherwise fall back to the legacy
 * activeRailItem-driven surface composition (edit mode + all rail items).
 */
function ActiveCanvasBody({ fallback }: { fallback: ReactNode }) {
  const studioMode = useStudioStore((s) => s.studioMode)
  const dispatchedOutput = useStudioStore((s) => s.dispatchedOutput)
  const currentProject = useStudioStore((s) => s.currentProject)
  const activeRailItem = useStudioStore((s) => s.activeRailItem)

  if (
    studioMode === 'build' &&
    activeRailItem === 'preview' &&
    (dispatchedOutput !== null || currentProject !== null)
  ) {
    return <BuildModeCanvas />
  }
  return <ActiveSurface fallback={fallback} />
}

/**
 * IDE shell composition (concept2 verbatim PR1 base).
 *
 * Layout:
 *   GroupedRail (w-14)
 *   └── TopBar (h-11, spans workspace + chat)
 *       └── canvas (children, flex-1) ─── ChatRail (w-80)
 *   BottomBar (h-8)
 *
 * Mounted by studio-shell.tsx when ideV2Enabled && view === 'workspace'.
 * The flag check lives in studio-shell, not here, so this component is
 * trivially testable and reusable in dev routes.
 *
 * Phase A A3.3 — Empty→Generating transition:
 *   `isEmpty` derived from `dispatchedOutput === null` (was: generationState).
 *   EmptyState is mounted only while the IDE is truly in build-idle mode.
 *   Keeping it mounted behind edit surfaces caused the generation-start page
 *   to remain in the paint/accessibility path during rail navigation.
 */
export function IDEShellLayout({ children }: Props) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const studioMode = useStudioStore((s) => s.studioMode)
  const dispatchedOutput = useStudioStore((s) => s.dispatchedOutput)
  const currentProject = useStudioStore((s) => s.currentProject)
  const projectLoadError = useStudioStore((s) => s.projectLoadError)
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  // Read for `boundaryKey` below — ActiveSurface branches on it within a
  // single rail item.
  const authorityDomain = useStudioStore((s) => s.chatContext?.authorityDomain)
  const hasBuildCanvas =
    studioMode === 'build' &&
    activeRailItem === 'preview' &&
    (dispatchedOutput !== null || currentProject !== null)
  const isEmpty = studioMode === 'build' && activeRailItem === 'preview' && !hasBuildCanvas

  // RUX-6 — the boundary key must change whenever the rendered subtree under
  // it changes, not only on a rail switch. ActiveCanvasBody swaps
  // BuildModeCanvas ⇄ ActiveSurface on (studioMode, dispatchedOutput) WITHOUT
  // changing activeRailItem (e.g. a build→edit mode switch on the same
  // "preview" rail, or a fresh dispatchedOutput arriving via the
  // outside-the-boundary ChatRail). Keying on activeRailItem alone left a
  // crashed BuildModeCanvas's errored fallback covering a healthy edit-mode
  // surface until Retry was clicked. The discriminator mirrors
  // ActiveCanvasBody's branch condition so the key flips exactly when the
  // subtree does. (Surfaces already unmount on every rail change, so the
  // added discriminator never discards live surface state.)
  const canvasBodyKind = hasBuildCanvas ? 'build-canvas' : 'surface'
  // Third term, same rule: ActiveSurface's `case 'source'` renders EITHER
  // AsIsView or SourceSurface depending on the authority domain, and As-Is
  // parks on the very same rail item as Design (DOMAIN_RENEWAL_RAIL has no
  // rail of its own for it). So an As-Is⇄Design swap moved neither of the two
  // terms above, and a crashed SourceSurface kept its fallback over a healthy
  // As-Is view until Retry was clicked — the exact failure the paragraph above
  // says this key exists to prevent.
  const surfaceVariant = authorityDomain === 'as-is' ? ':as-is' : ''
  const boundaryKey = `${activeRailItem}:${canvasBodyKind}${surfaceVariant}`
  // ── Breakpoint-aware shell root (Phase 531, SPEC §4 / D-04): single bp source ──
  const { bp, isCompact } = useUIBreakpoint()
  const preserveWorkspaceWidth = !isCompact && !['preview', 'evidence', 'source'].includes(activeRailItem)

  // ── Global keyboard shortcut bus (SPEC §3.5): one listener for the shell's lifetime ──
  useEffect(() => {
    registerShortcuts()
    return () => unregisterShortcuts()
  }, [])

  return (
    <div data-bp={bp} className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      <TopBar />
      <div className="flex min-h-0 flex-1">
        <GroupedRail />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-1 min-h-0 min-w-0" data-testid="ide-shell-content">
            {/* relative wrapper so InspectorOverlay (absolute) anchors to canvas region */}
            {/* Now also a flex column so DevToolsDock occupies the bottom slot */}
            <main className="relative flex-1 min-w-0 bg-background flex flex-col">
              {projectLoadError ? (
                <div className="flex h-full items-center justify-center p-4" data-testid="project-load-error">
                  <div className="max-w-sm rounded-md border border-destructive/50 bg-destructive/5 p-4">
                    <h2 className="text-sm font-semibold text-foreground">{copy.couldNotLoadProject}</h2>
                    <p className="mt-1 text-xs text-muted-foreground">{projectLoadError}</p>
                    <button
                      type="button"
                      onClick={() => {
                        useStudioStore.getState().closeProject()
                      }}
                      className="mt-3 rounded-md border border-border bg-card px-3 py-1 text-sm text-foreground hover:bg-accent"
                    >
                      {copy.backToProjects}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <CockpitAttentionHandoff />
                  <div
                    className={`relative flex-1 min-h-0 ${preserveWorkspaceWidth ? 'overflow-x-auto overflow-y-hidden' : 'overflow-hidden'}`}
                    data-testid="active-workspace-scroll"
                    data-workspace-scroll={preserveWorkspaceWidth ? 'horizontal' : 'none'}
                  >
                    {isEmpty && (
                      <div className="absolute inset-0 opacity-100" aria-hidden={false} data-testid="empty-state-layer">
                        <EmptyState />
                      </div>
                    )}
                    <div
                      className={`${preserveWorkspaceWidth ? 'absolute inset-y-0 left-0 w-full min-w-[960px]' : 'absolute inset-0'} transition-opacity duration-200 ${isEmpty ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                      aria-hidden={isEmpty}
                      inert={isEmpty ? true : undefined}
                      data-testid="active-canvas-layer"
                    >
                      {/* F10 (Lane 2) — one boundary at the surface dispatch
                        point isolates all 11 rail surfaces + BuildModeCanvas:
                        a throwing surface degrades to a categorized fallback
                        while TopBar/GroupedRail/ChatRail/BottomBar stay alive.
                        `key={boundaryKey}` (rail item + canvas-body kind)
                        remounts (resets) the boundary whenever the rendered
                        subtree changes — rail switch OR a build⇄edit /
                        dispatch-driven BuildModeCanvas⇄ActiveSurface swap on
                        the SAME rail (RUX-6) — since ErrorBoundary has no
                        resetKeys prop and an errored boundary otherwise stays
                        errored. The ActiveSurface switch already unmounts
                        surfaces on every rail change, so the key adds no state
                        loss; if surfaces are ever kept alive across switches,
                        this key would start discarding that state. */}
                      <ErrorBoundary key={boundaryKey} fallbackLabel={`${activeRailItem} surface`}>
                        <ActiveCanvasBody fallback={children} />
                      </ErrorBoundary>
                    </div>
                    <InspectorOverlay />
                    <ResourcesDrawer />
                    <ExpressionLabDrawer />
                    {isEmpty && <OnboardingTour />}
                  </div>
                  <DevToolsDock />
                </>
              )}
            </main>
            <ChatRail />
          </div>
          <BottomBar />
        </div>
      </div>
    </div>
  )
}
